import { ChangeDetectorRef, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ConfigurationService } from '@config/configuration.service';
import { PaginatedRequest } from '../../../../app.interface';
import { UserListI, UserDetailI, CreateUserI, ActiveUserCountResponse, OperatorClientMappingData, ClientOperatorMapping, AssignOperatorClientsResponse, OperatorData } from './user-management.interfaces';
import { map, tap } from 'rxjs/operators'
import { UserDetail } from './user-management.model';
import { GenericResponse, AuthService, AuthRole } from '../../../auth';
import { BehaviorSubject } from 'rxjs';
import { EventsService } from '@app/_shared';
import { CustomEvents } from '@app/_helpers';

@Injectable({
  providedIn: 'root'
})
export class UserManagementService {
  activeUsersCount = 0;
  operatorClientMappingData = new Map<string,OperatorClientMappingData>();

  constructor(
    private http: HttpClient,
    private conf: ConfigurationService,
    private authService: AuthService,
    private configurationService: ConfigurationService,
    private eventService: EventsService
  ) {
    if (this.authService.accessTokenObj?.role === AuthRole.ADMIN) {
      this.getActiveUsersCount().subscribe(count => {
        this.activeUsersCount = count.count;
      })
    }
  }

  getUserList(pageNumber = 1, pageSize = 50, userQuery = null, lockedUser = false) {
    const req: PaginatedRequest = {
      page_number: pageNumber,
      page_size: pageSize
    }

    if (userQuery) {
      req.query = userQuery;
    }
    req.lockedUser = lockedUser;
    return this.http.get<UserListI[]>(this.conf.applicationUrls.ums, {
      params: req as any
    });
  }

  getUserDetails(clientId: string) {
    return this.http.get<UserDetailI>(this.conf.applicationUrls.ums + clientId + '/')
      .pipe(
        map(detail => new UserDetail(detail))
      );
  }

  createUser(createClientReq: CreateUserI) {
    return this.http.post<UserDetailI>(this.conf.applicationUrls.ums, createClientReq);
  }

  updateUser(updateClientReq: CreateUserI) {
    return this.http.put<UserDetailI>(this.conf.applicationUrls.ums, updateClientReq);
  }

  deleteClient(clientID: string) {
    return this.http.delete<GenericResponse>(this.conf.applicationUrls.ums + clientID + '/')
  }

  getActiveUsersCount() {
    return this.http.get<ActiveUserCountResponse>(this.conf.applicationUrls.activeUsers);
  }

  toggleAlgoProvider(clientId: string, state: boolean) {
    let params = new HttpParams().set('enable', state.toString());
    return this.http.put<GenericResponse>(this.conf.applicationUrls.toggleAlgoProvider + clientId, null, {params});
  }

  setCache(key: string, data: string) {
    return this.http.post(this.conf.applicationUrls.setCache, {
      key,
      data
    });
  } 

  getCache(key: string) {
    return this.http.get<any>(this.conf.applicationUrls.getCache, {
      params: {
        key: key
      }
    })
  }

  deleteRetainMultiplier() {
    return this.http.delete(this.conf.applicationUrls.retainMultiplier);
  }

  getOperatorData() {
    return this.http.get(this.configurationService.applicationUrls.getOperatorClientMappings).pipe(tap((res: Record<string, OperatorClientMappingData>) => {
      Object.keys(res['operatorClientMappings']).forEach(key => {
        this.operatorClientMappingData.set(key, {
          name: res['operatorClientMappings'][key].name,
          plan: res['operatorClientMappings'][key].plan,
          clients: res['operatorClientMappings'][key].clients,
          source_identifier: res['operatorClientMappings'][key].source_identifier
        });
      });
      this.eventService.publish(CustomEvents.clientOperatorDataUpdated);
    }))
  }

  /**
   * Gets a list of all operators from the operatorClientMappingData
   * @returns Array of OperatorData objects containing operator information
   * 
   * This function:
   * 1. Creates an empty array to store operator data
   * 2. Iterates through the operatorClientMappingData Map
   * 3. For each operator entry, creates an OperatorData object with:
   *    - name: Operator's name
   *    - client_id: Operator's client ID
   *    - plan: Operator's plan
   *    - source_identifier: Operator's source identifier
   * 4. Returns the array of all operators
   */
  getOperatorList(): OperatorData[] {
    let operatorList: OperatorData[] = [];
    this.operatorClientMappingData.forEach((operator_client_data, operator_client_id) => {
      operatorList.push({
        name: operator_client_data.name,
        client_id: operator_client_id,
        plan: operator_client_data.plan,
        source_identifier: operator_client_data.source_identifier
      });
    });
    return operatorList;
  }

  /**
   * Gets the mapping between clients and operators
   * @returns An object containing client IDs as keys and their mapped operator IDs as values
   * 
   * This function:
   * 1. Creates an empty object to store client-operator mappings
   * 2. Iterates through the operatorClientMappingData Map
   * 3. For each operator, iterates through their client list
   * 4. Maps each client ID to their operator's ID in the result object
   */
  clientOperatorMappingObject() {
    let clientOperatorMappingObject = {};
    this.operatorClientMappingData.forEach((operator_client_data, operator_client_id) => {
      operator_client_data?.clients?.forEach(client => {
        if (client?.client_id) {
          clientOperatorMappingObject[client.client_id] = operator_client_id;
        }
      });
    });
    return clientOperatorMappingObject;
  }

  /**
   * Updates the operator-client mapping data when a client is mapped to an operator
   * @param user The client user being mapped to an operator
   * @param operator The operator to which the client is being mapped
   * 
   * This function:
   * 1. Removes the client from previous operator's client list if any mapping exists
   * 2. Updates the clientOperatorMappingObject with new operator mapping
   * 3. Adds the client to the new operator's client list if not already present
   * 4. Publishes an event to notify the mapping update
   */
  updateClientOperatorMappingData(user: UserListI, operator: OperatorData) {
    let previousOperatorId = this.clientOperatorMappingObject[user.client_id];
    if (previousOperatorId) {
      this.operatorClientMappingData.get(previousOperatorId).clients = this.operatorClientMappingData.get(previousOperatorId).clients?.filter(client => client.client_id !== user.client_id);
      delete this.clientOperatorMappingObject[user.client_id];
    } 
    this.clientOperatorMappingObject[user.client_id] = operator.client_id;
    if (!(this.operatorClientMappingData.get(operator.client_id)?.clients?.filter(client => client.client_id === user.client_id).length)) {
      this.operatorClientMappingData.get(operator.client_id).clients.push(user);
    }
    this.eventService.publish(CustomEvents.clientOperatorDataUpdated);
  }

  assignOperatorClients(operatorClientMap: ClientOperatorMapping[]) {
    return this.http.post<AssignOperatorClientsResponse[]>(
      this.configurationService.applicationUrls.assignOperatorClients, 
      operatorClientMap,
    );
  }

  makeClientOperator(clientId: string, state: boolean) {
    return this.http.post(this.conf.applicationUrls.makeClientOperator, {
      client_id: clientId,
      is_operator: state
    });
  }

  operatorPrivilegeAdded(operatorData: OperatorData) {
    this.operatorClientMappingData.set(operatorData.client_id, {
      name: operatorData.name,
      clients: [],
      plan: operatorData.plan,
      source_identifier: operatorData.source_identifier
    });
    this.eventService.publish(CustomEvents.clientOperatorDataUpdated);
  }

  operatorPrivilegeRemoved(operatorId: string) {    
    this.operatorClientMappingData.delete(operatorId);
    
    this.eventService.publish(CustomEvents.clientOperatorDataUpdated);
  }
}
